// @flow
import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
} from "reselect";
import { has } from "../../utils/helpers/objects";

import Href from "../../models/href/Href";
import ContentModel from "../../models/content/ContentModel";
import ContentTOCModel from "../../models/content/ContentTOCModel";
import ApplicationModel from "../../models/application/ApplicationModel";
import TabModel from "../../models/tab/TabModel";
import CaseViewModel from "../../models/caseview/CaseViewModel";
import FormModel from "../../models/form/FormModel";

import type { ReduxState, PreferenceValue } from "../types";
import type { ModularUIModel } from "../../models/types";
import { MODULARUI_STATUS } from "../../constants";

/**
 */
export const getAllModelsByInstance = <T>(
  state: ReduxState,
  instance: Class<T>,
): Array<ModularUIModel> => {
  const locale = state?.i18n?.locale || "en";
  const modularui = state?.modularui;

  if (modularui) {
    return Object.keys(modularui)
      .filter((key) => {
        const { model } = modularui[key];
        return model instanceof instance && model.locale === locale;
      })
      .map((key) => modularui[key].model);
  }

  return [];
};

/**
 */
export const getFirstModelByInstance = <T = ModularUIModel>(
  state: ReduxState,
  instance: Class<T>,
): T | null => {
  const locale = state?.i18n?.locale || "en";
  const modularui = state?.modularui;

  if (!modularui) return null;

  const foundKey = Object.keys(modularui).find((key) => {
    const entry = modularui[key];
    if (!entry || !entry.model) return false;

    const isInstance = entry.model instanceof instance;
    const localeMatches = entry.model.locale === locale;

    return isInstance && localeMatches;
  });

  // $FlowIssue
  return foundKey ? modularui[foundKey].model : null;
};

/**
 */
export const getActiveModelByInstance = <T>(
  state: ReduxState,
  instance: Class<T>,
): T | null => {
  const location = getLocation(state);

  const locale = state?.i18n?.locale || "en";
  const modularui = state?.modularui;

  if (modularui) {
    const key = Object.keys(modularui).find((key) => {
      const { model } = modularui[key];
      return (
        model instanceof instance &&
        model.locale === locale &&
        location.startsWith(model.selfhref.path)
      );
    });

    if (key) {
      const { model } = modularui[key];
      if (model instanceof instance) {
        return model;
      }
    }
  }

  return null;
};

/**
 * Get the application model, which is the model with selfhref '/'
 */
export const getApplication = (state: ReduxState): null | ApplicationModel =>
  getFirstModelByInstance(state, ApplicationModel);

/**
 */
export const getTab = (state: ReduxState): null | TabModel =>
  getFirstModelByInstance(state, TabModel);

/**
 */
export const getCaseView = (state: ReduxState): null | CaseViewModel =>
  getFirstModelByInstance(state, CaseViewModel);

/**
 */
export const getForm = (state: ReduxState): null | FormModel =>
  getFirstModelByInstance(state, FormModel);

/**
 * Get the model by it's href
 */
export const modelByHref = (
  state: ReduxState,
  href: Href | string,
): null | ModularUIModel => {
  if (state && state.modularui) {
    const findHref = href instanceof Href ? href : new Href(href);

    const modelConfigKey = Object.keys(state.modularui).find((key) => {
      const model = state.modularui[key];

      return (
        model.model &&
        model.model.selfhref &&
        model.model.selfhref.equals(findHref)
      );
    });

    if (modelConfigKey) {
      return state.modularui[modelConfigKey].model;
    }
  }

  return null;
};

/**
 * Return the key of a model by the selfhref the model is saved in the reducer
 */
export const keyByHref = (state: ReduxState, href: Href | string): ?string => {
  if (state && state.modularui) {
    const findHref = href instanceof Href ? href : new Href(href);

    return Object.keys(state.modularui).find((key) => {
      const { model } = state.modularui[key];
      return model && model.selfhref && model.selfhref.equals(findHref);
    });
  }

  return null;
};

/**
 * Returns all model keys found in the store, restrict on href
 */
export const allKeysByHref = (
  state: ReduxState,
  href: Href | string,
): Array<string> => {
  if (state && state.modularui) {
    const findHref = href instanceof Href ? href : new Href(href);

    return Object.keys(state.modularui).filter((key) => {
      const { model } = state.modularui[key];
      return model && model.selfhref && model.selfhref.equals(findHref);
    });
  }

  return [];
};

/**
 */
const getLocation = (state: ReduxState): string =>
  state?.router?.location.pathname;

/**
 */
const getModels = (state: ReduxState) => {
  const models = [];

  Object.keys(state.modularui).forEach((modelKey: string) => {
    const modelEntry = state.modularui[modelKey];

    if (has(modelEntry, "model")) {
      const { key, selfhref, label, type } = modelEntry.model;

      const modelInfo = {
        key,
        selfhref,
        label,
        type,
        selfContentLink: null,
      };

      if (
        modelEntry.model instanceof ContentModel ||
        modelEntry.model instanceof ContentTOCModel
      ) {
        models.push({
          ...modelInfo,
          selfContentLink: modelEntry.model.selfContentLink,
        });
      } else {
        models.push(modelInfo);
      }
    }
  });

  return models;
};

export const getActiveModels: (state: ReduxState) => Array<{
  key: string,
  href: Href,
  label: string,
  type: string,
}> = createSelectorCreator(
  defaultMemoize,
  (value, other) => JSON.stringify(value) === JSON.stringify(other),
)([getLocation, getModels], (location, models) => {
  const contextModels = [];

  if (location) {
    const locationParts = location.split("/");

    locationParts.reduce((accumulator, current) => {
      const path = `${accumulator}/${current}`;

      // Remove modelcatalog part to match breadcrumb parts
      const comparePath = decodeURIComponent(path).replace(
        "/modelcatalog/",
        "/",
      );

      const foundEntry = models.find(
        (model) => model.selfhref && model.selfhref.equals(comparePath),
      );

      if (foundEntry) {
        const { key, label, type, selfhref, selfContentLink } = foundEntry;

        const href =
          path.startsWith("/modelcatalog/") && selfContentLink
            ? new Href(`/modelcatalog${selfContentLink.encodedHref.toString()}`)
            : selfhref;

        contextModels.push({ key, href, label, type });
      }

      return path;
    });
  }

  return contextModels;
});

/**
 */
export const getPreference = (
  state: ReduxState,
  preferenceName: string,
): null | PreferenceValue => {
  if (state && state.preferences) {
    return state.preferences[preferenceName];
  }

  return null;
};

/**
 * @deprecated - Use getFirstModelByInstance or the appropriate getter for the model type: getApplication, getTab, etc
 */
export const getModelsByType = (
  state: ReduxState,
  type: string,
): Array<ModularUIModel> => {
  if (state && state.modularui) {
    return Object.keys(state.modularui)
      .filter(
        (key) =>
          state.modularui[key] &&
          state.modularui[key].model &&
          state.modularui[key].model.type === type,
      )
      .map((key) => state.modularui[key].model);
  }

  return [];
};

/**
 */
export const getAllFinishedModels: (
  state: ReduxState,
) => Array<ModularUIModel> = createSelector(
  [(state) => state.modularui, (state) => state.i18n.locale],
  (modularui, locale): Array<ModularUIModel> => {
    if (modularui) {
      return Object.keys(modularui)
        .filter((key) => {
          if (modularui[key]) {
            const item = modularui[key];
            return (
              item.status === MODULARUI_STATUS.FINISHED &&
              item.model?.locale === locale
            );
          }
          return false;
        })
        .map((key) => modularui[key].model);
    }
    return [];
  },
);
